<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/Overview';
    let nextLink= '/Tutorial/Making_an_Editor';
    
    import Note from '$lib/notes/Note.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="creating-your-metamodel-1"
  bind:intersecting={visibleById["creating-your-metamodel-1"]}
  onIntersectChange={(visible) => setVisible("creating-your-metamodel-1", visible)}
>Creating your Metamodel</SectionComponent>
<p>From this point onward, we assume you have a Freon project open in your
favorite IDE—either by cloning the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
>Example project</a>
or the <a
  href="/Documentation/Overview/Getting_Started#creating-a-project-2"
>Template project</a>.</p>
<SectionComponent tag="h2" 
  id="defining-the-model-and-model-units-2"
  bind:intersecting={visibleById["defining-the-model-and-model-units-2"]}
  onIntersectChange={(visible) => setVisible("defining-the-model-and-model-units-2", visible)}
>Defining the model and model units</SectionComponent>
<p>The first thing we need to decide is the name of our language and how we
want to partition any model the user creates. The user will see only one partition
at a time in the editor, but references can point to nodes in other partitions. In
Freon terminology these partitions are called <em>Model Units</em>.</p>
<p>We’ll create a model with a number of topics. Each model is dedicated to
one of the courses that our client offers (i.e., one of their websites). Each model is divided into four
parts. The <strong>Topics</strong> part covers the different webpages available
for the topic. <strong>Flow</strong> defines how navigation between
pages should proceed. The <strong>Tests</strong> section defines tests for
topics and the flow between topics. Finally, the <strong>SiteGroup</strong> part provides an overview of
a site group with all subtopics, flows, and tests relevant to that topic.</p>
<p>Create the file <code>edu-main.ast</code> in the <code>src/defs</code> folder, and add the following code.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-main.ast#L1-L10

language Education

model Education /* Computer Aided Learning */ &#123;
    name: identifier;
    topic: Topic[];
    flow: Flow[];
    tests: Test[];
    overviews: SiteGroup[];
&#125;</code></pre>
<p>If you’re impatient and already tried to generate the editor, you’ll have noticed
that there are errors in our input. We need to define the concepts <code>Topic</code>,
<code>Flow</code>, <code>Test</code>, and <code>SiteGroup</code>. All four are model units, so we define them as such.</p>
<pre class="language-freon"><code class="language-freon">modelunit Topic &#123;

&#125;
modelunit Test &#123;

&#125;
modelunit Flow &#123;

&#125;
modelunit SiteGroup &#123;

&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-sitegroup-model-unit-3"
  bind:intersecting={visibleById["the-sitegroup-model-unit-3"]}
  onIntersectChange={(visible) => setVisible("the-sitegroup-model-unit-3", visible)}
>The <em>SiteGroup</em> model unit</SectionComponent>
<p>Let’s focus on the <em>SiteGroup</em> model unit first. Because it’s likely that we’ll need to reference topics,
we give each topic a <code>name</code> of type <code>identifier</code>.</p>
<Note>{#snippet header()} The type identifier versus the type string.{/snippet}  
{#snippet content()}
<p>A property of type string may contain any printable character, but the content of an identifier is bound to a number of rules.
These rules are equal to the rules in Typescript. Any concept or model unit that has a property
<i>name</i> of type <i>identifier</i> can be referred to.</p>
{/snippet}
</Note>
<p>Every (sub)topic belongs to a site group, but we don’t want this relationship
to be like a UML aggregation, as that would be too restrictive here. Therefore, we introduce another feature of
Freon’s metamodel: the <strong>reference</strong>. You can compare a reference to a UML directed association, where the role name is the property name (<code>topics</code>), and the direction
is from the owner of the property (<code>SiteGroup</code>) to the property’s type (<code>Topic</code>). The square brackets after the type indicate
a list (multiple instances).</p>
<p>Likewise, we define references to the flows and tests that are part of this site group.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-main.ast#L11-L17

modelunit SiteGroup &#123;
    name: identifier;
    description: string; /* e.g. Mathematics, fractions for students age 10 */
    reference topics: Topic[];
    reference flows: Flow[];
    reference tests: Test[];
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-topic-model-unit-4"
  bind:intersecting={visibleById["the-topic-model-unit-4"]}
  onIntersectChange={(visible) => setVisible("the-topic-model-unit-4", visible)}
>The <em>Topic</em> model unit</SectionComponent>
<p>To avoid very large files, you can split your language definition across as many files as you like.
As long as the file extension is <code>.ast</code>, the file
will be included as part of your language metamodel.</p>
<p>So, let’s create another file to store the metamodel for the <em>Topic</em> model unit. Like the <em>SiteGroup</em> model
unit, the <em>Topic</em> model unit has a name of type <code>identifier</code>. A description is probably
useful as well; we give it the type <code>string</code>, because it may contain any characters.
We’re modeling a website, so the concept <strong>Page</strong> should definitely be present.
Each <code>Topic</code> will have a number of pages. The result for now is the code below.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-topics.ast#L3-L8

modelunit Topic &#123;
    name: identifier;
    reference main: SiteGroup;
    description: string;
    pages: Page[];
&#125;</code></pre>
<p>So far, so good!</p>
<p>Now, let’s outline the types of pages we’ll use.
There will be ordinary pages with explanatory text and
questions to test understanding. We also want content that’s engaging for kids—for instance, videos.
There should be pages with examples and pages with assignments. It’s a good
idea to make the <strong>Page</strong> concept abstract, and then define several concepts that inherit from it.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-topics.ast#L10-L39

abstract concept Page &#123;
    name: identifier;
    questions: Question[];
&#125;

concept Theory base Page &#123;
    /* For the sake of the example this is simplified.
    Should be formatted text including pictures, etc. */
    content: Line[];
&#125;

concept Line &#123;
    content: string;
&#125;

concept Video base Page &#123;
    url: string;
&#125;

concept WorkSheet base Page &#123;
&#125;

concept ExamplePage base Page &#123;
    content: Line[];
&#125;

concept InDepthMaterial base Page &#123;
    content: Line[];
&#125;</code></pre>
<Note header={header2} content={content2}> 
{#snippet header2()}UML diagrams{/snippet}
{#snippet content2()}
<p>The Freon metamodel can be expressed in UML. For instance, a Freon concept may be compared to a UML class. 
It can have properties and associations with other concepts,
and it can inherit from other concepts and/or implement an interface.</p>
<p>Likewise, reference properties may be compared to UML directed associations, while normal properties resemble aggregation relations.</p>
<p>Freon automatically generates several UML class diagrams of your metamodel. You can find them in the
folder <code>src/diagrams</code>, along with other diagrams
(for example, one that focuses on inheritance relations).</p>
{/snippet}
</Note>
<p>But let’s not forget to add a definition for the concept <strong>Question</strong>. Freon will complain if you
use concepts you haven’t defined. And because we’re dealing with mathematics, we need to
represent fractions; a simple number won’t be sufficient.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-topics.ast#L41-L57

    name: identifier;
    content: string;
    correctAnswer: NumberConcept;
&#125;

abstract concept NumberConcept &#123;
&#125;

concept SimpleNumber base NumberConcept &#123;
    value: number;
&#125;

concept Fraction base NumberConcept &#123;
    numerator: number;
    denominator: number;
&#125;</code></pre>
<p>By now, you’ve seen the gist of how to build a simple metamodel. For the sake of brevity,
we will not go further into defining the content of each page type. Let’s just assume
there is some content, represented here as lines of text.</p>
<p>If you like, you can generate the editor for our DSL and try it out. Use the following command in the terminal
window of your IDE (you can swap <code>npm</code> for the package manager of your choice):</p>
<pre class="language-bash"><code class="language-bash">npm run build</code></pre>
<p>In the GitHub project, we’ve provided an example model named <code>lesson1</code>.
Before you open the editor, start the server.</p>
<pre class="language-bash"><code class="language-bash">npm run start</code></pre>
<p>Open the editor, select <code>lesson1</code> as the model, and have a browse.</p>
<p>Yes, we know—it works, but it doesn’t look great. In the next lesson we’ll learn how to
make the model look more polished in the editor. But first, we’ll define the second model unit:
the description of the flow between pages.</p>
<SectionComponent tag="h2" 
  id="the-flow-model-unit-5"
  bind:intersecting={visibleById["the-flow-model-unit-5"]}
  onIntersectChange={(visible) => setVisible("the-flow-model-unit-5", visible)}
>The <em>Flow</em> model unit</SectionComponent>
<p>Create a second file called <code>edu-flow.ast</code>. This file will contain the part of the metamodel that handles
<strong>Flows</strong>.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-flow.ast#L1-L6

language Education

modelunit Flow &#123;
    reference main: SiteGroup;
    rules: FlowRule[];
&#125;</code></pre>
<p>Here again, we use a reference to link the flow to a site group.</p>
<p>In the following code, each <strong>FlowRule</strong> is linked to a specific page—the page the pupil is currently
working on. The flow rule determines which page to show next using a set of <strong>PageTransitions</strong>.
A page transition is a condition paired with a target page. If the condition is fulfilled, that
page will be the next in the flow.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-flow.ast#L8-L18

concept FlowRule &#123;
    name: identifier;
    description: string;
    reference page: Page;
    transitions: PageTransition[];
&#125;

concept PageTransition &#123; /* E.g. Grade A =&gt; show pageA, Grade F =&gt; show pageC */
    condition: Grade;
    reference toPage: Page;
&#125;</code></pre>
<p>But how do we define the condition for a page transition? For now, we’ll take the straightforward approach and use an enumeration.
In Freon terminology this is a <a
  href="/Documentation/Creating_the_Metamodel/Language_Structure#limited-concept-7"
>limited concept</a>,
which is slightly more expressive than a traditional enumeration.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson1-defs/edu-flow.ast#L20-L27

limited Grade &#123;
    gradeA;
    gradeB;
    gradeC;
    gradeD;
    gradeE;
    gradeF;
&#125;</code></pre>
<p>That’s the second model unit done!</p>
<p>Again, you might want to generate the editor and try it out. Note that when you start the editor, it will open
with the topic from the previous step. Click the arrow-left icon in the top bar to open an
overview of all model units present in your project. Click on <code>StartFlow</code>, select <strong>Open</strong> from the dropdown,
and you can view and edit the partition we’ve prepared for you. Or play with the File menu—click <strong>New Model Unit</strong> and explore.</p>
<p>Things still don’t look great, do they? Please be patient. In the next lesson you’ll learn to beautify the model’s appearance in the editor.</p>


<PrevNextSection {prevLink} {nextLink} />